import { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Mic, Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import DictableTextarea from './DictableTextarea';

const PASSI = [
  { id: 'generali', titolo: 'Condizioni generali del turno', suggerimento: 'Come stanno gli ospiti: alimentazione, idratazione, riposo, umore, mobilizzazione.' },
  { id: 'criticita', titolo: 'Criticita e problemi', suggerimento: 'Peggioramenti, parametri alterati, cadute, rifiuti di terapia, chiamate al medico.' },
  { id: 'controlli', titolo: 'Da controllare nel turno successivo', suggerimento: 'Medicazioni, esami, visite, parametri da ricontrollare, familiari da richiamare.' },
];

export default function ConsegnaVocale({ giorno, turno, area, onSaved }) {
  const [open, setOpen] = useState(false);
  const [passo, setPasso] = useState(0);
  const [testi, setTesti] = useState({ generali: '', criticita: '', controlli: '' });
  const [salvando, setSalvando] = useState(false);

  const corrente = PASSI[passo];
  const set = (v) => setTesti(t => ({ ...t, [corrente.id]: v }));

  const componi = () => PASSI
    .filter(p => testi[p.id].trim())
    .map(p => `${p.titolo.toUpperCase()}: ${testi[p.id].trim()}`)
    .join('\n');

  const salva = async () => {
    const contenuto = componi();
    if (!contenuto) return toast.error('Detta almeno una sezione prima di salvare');
    setSalvando(true);
    try {
      await api.post('/consegna/note', {
        data: giorno, turno, area, contenuto,
        criticita: !!testi.criticita.trim(),
      });
      toast.success('Consegna vocale registrata');
      setOpen(false); setPasso(0); setTesti({ generali: '', criticita: '', controlli: '' });
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel salvataggio'); }
    setSalvando(false);
  };

  return (
    <>
      <Button variant="outline" size="sm" className="gap-2" onClick={() => setOpen(true)} data-testid="consegna-vocale-btn">
        <Mic className="w-4 h-4" />Consegna vocale guidata
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Consegna vocale guidata · passo {passo + 1} di {PASSI.length}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              {PASSI.map((p, i) => (
                <Badge key={p.id} className={`text-[10px] ${i === passo ? 'bg-brand text-white' : testi[p.id].trim() ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {i + 1}. {p.titolo}
                </Badge>
              ))}
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-800">{corrente.titolo}</p>
              <p className="text-xs text-slate-500 mb-2">{corrente.suggerimento}</p>
              <DictableTextarea rows={5} value={testi[corrente.id]} onChange={e => set(e.target.value)}
                placeholder="Premi il microfono e parla, poi correggi il testo se serve"
                contesto={`Consegna di turno: ${corrente.titolo}`}
                className="text-sm" data-testid={`consegna-vocale-${corrente.id}`} />
            </div>
            {componi() && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Anteprima della nota</p>
                <pre className="text-[11px] text-slate-600 whitespace-pre-wrap font-sans" data-testid="consegna-vocale-anteprima">{componi()}</pre>
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <Button variant="outline" size="sm" disabled={passo === 0} onClick={() => setPasso(p => p - 1)} data-testid="consegna-vocale-indietro">
                Indietro
              </Button>
              <div className="flex gap-2">
                {passo < PASSI.length - 1 && (
                  <Button variant="outline" size="sm" className="gap-1" onClick={() => setPasso(p => p + 1)} data-testid="consegna-vocale-avanti">
                    Avanti<ArrowRight className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button size="sm" onClick={salva} disabled={salvando} className="bg-brand hover:bg-brand-dark text-white gap-1" data-testid="consegna-vocale-salva">
                  <Check className="w-3.5 h-3.5" />{salvando ? 'Salvataggio...' : 'Registra consegna'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
